import { Logger } from "@hiddentao/logger"
import {
  ClientDataEvents,
  type ClientDataStorageAdapter,
  ClientDataStorageEngine,
  ContentProcessor,
  DEFAULT_USER_SETTINGS,
  type DecryptedItemData,
  type Note,
  SyncItemType,
  SyncMessageType,
  type Tag,
  type Task,
  type UserSettings,
} from "@taskfortress/core"
import { EventEmitter } from "@taskfortress/core/src/events"
import { PendingAsyncActions } from "@taskfortress/core/src/utils/async"
import { SocketConnectionStatus } from "../SocketProvider"

// Define events that DataEngine emits
export enum DataEngineEvents {
  USER_LOGGED_IN = "user-logged-in",
  USER_LOGGED_OUT = "user-logged-out",
  NOTES_UPDATED = "notes-updated",
  TASKS_UPDATED = "tasks-updated",
  TAGS_UPDATED = "tags-updated",
  SETTINGS_UPDATED = "settings-updated",
}

// Event map for DataEngine
type DataEngineEventMap = {
  [DataEngineEvents.USER_LOGGED_IN]: () => void
  [DataEngineEvents.USER_LOGGED_OUT]: () => void
  [DataEngineEvents.NOTES_UPDATED]: (notes: Note[]) => void
  [DataEngineEvents.TASKS_UPDATED]: (tasks: Task[]) => void
  [DataEngineEvents.TAGS_UPDATED]: (tags: Tag[]) => void
  [DataEngineEvents.SETTINGS_UPDATED]: (settings: UserSettings) => void
}

export interface DataEngineConfig {
  storageProvider: ClientDataStorageAdapter
  pendingActions: PendingAsyncActions
  logger: Logger
}

export class DataEngine extends EventEmitter<DataEngineEventMap> {
  private dataStorage: ClientDataStorageEngine
  private itemProcessor: ContentProcessor
  private logger: Logger
  private pendingActions: PendingAsyncActions

  // Current authentication state
  private userIsLoggedIn = false
  private encryptionKey: Uint8Array | null = null
  private connectionStatus: SocketConnectionStatus =
    SocketConnectionStatus.DISCONNECTED

  private sendCallback:
    | ((messageType: SyncMessageType, data: any) => void)
    | null = null

  constructor(config: DataEngineConfig) {
    super()

    this.logger = config.logger
    this.pendingActions = config.pendingActions
    this.dataStorage = new ClientDataStorageEngine(
      config.storageProvider,
      this.logger,
    )
    this.itemProcessor = new ContentProcessor({
      dataStorage: this.dataStorage,
      pendingActions: this.pendingActions,
      isAuthenticated: false,
      encryptionKey: null,
      connectionStatus: this.connectionStatus as string,
      sendUpdate: () => {
        // No-op send callback for initial state
      },
      logger: this.logger,
    })

    // Listen to ClientData events to update state
    this.setupClientDataListeners()
  }

  public get isLoggedIn(): boolean {
    return this.userIsLoggedIn
  }

  /**
   * User has logged in, setup data storage and emit login event
   */
  async userHasLoggedIn(
    email: string,
    encryptionKey: Uint8Array,
    connectionStatus: SocketConnectionStatus,
    sendCallback: (messageType: SyncMessageType, data: any) => void,
  ): Promise<void> {
    this.logger.info("DataEngine: User has logged in", { email })

    try {
      this.userIsLoggedIn = true
      this.encryptionKey = encryptionKey
      this.connectionStatus = connectionStatus
      this.sendCallback = sendCallback

      // Recreate instances with new auth state
      this.dataStorage = new ClientDataStorageEngine(
        this.dataStorage["storageAdapter"],
        this.logger,
      )
      this.itemProcessor = new ContentProcessor({
        dataStorage: this.dataStorage,
        pendingActions: this.pendingActions,
        isAuthenticated: true,
        encryptionKey: encryptionKey,
        connectionStatus: connectionStatus as string,
        sendUpdate: sendCallback,
        logger: this.logger,
      })

      // Re-setup listeners with new dataStorage instance
      this.setupClientDataListeners()

      // Initialize data storage
      await this.dataStorage.initialize(encryptionKey, email)

      // Ensure global tags exist
      await this.itemProcessor.ensureGlobalTagsExist(true)

      this.logger.info("DataEngine: Storage initialized")

      // Emit login event
      this.emit(DataEngineEvents.USER_LOGGED_IN)

      // Load and emit initial data
      await this.loadAndEmitAllData()
    } catch (error) {
      this.logger.error("DataEngine: Failed to initialize storage", { error })
      throw error
    }
  }

  /**
   * User has logged out, clear data and emit logout event
   */
  async userHasLoggedOut(): Promise<void> {
    this.logger.info("DataEngine: User has logged out")

    this.userIsLoggedIn = false
    this.encryptionKey = null
    this.connectionStatus = SocketConnectionStatus.DISCONNECTED
    this.sendCallback = null

    this.emit(DataEngineEvents.USER_LOGGED_OUT)

    this.emit(DataEngineEvents.NOTES_UPDATED, [])
    this.emit(DataEngineEvents.TASKS_UPDATED, [])
    this.emit(DataEngineEvents.TAGS_UPDATED, [])
    this.emit(DataEngineEvents.SETTINGS_UPDATED, DEFAULT_USER_SETTINGS)
  }

  /**
   * Update connection status and send callback
   */
  updateConnectionState(
    connectionStatus: SocketConnectionStatus,
    sendCallback: (messageType: SyncMessageType, data: any) => void,
  ): void {
    this.connectionStatus = connectionStatus
    this.sendCallback = sendCallback

    if (this.userIsLoggedIn && this.encryptionKey) {
      this.itemProcessor.updateDependencies(
        this.userIsLoggedIn,
        this.encryptionKey,
        connectionStatus as string,
        sendCallback,
        this.logger,
      )
    }
  }

  /**
   * Save items with debounced updates
   */
  async saveItems(
    items: Array<{ itemId: string; type: SyncItemType; content: any }>,
  ): Promise<void> {
    if (!this.userIsLoggedIn) {
      throw new Error("User must be authenticated to save items")
    }

    if (!this.encryptionKey) {
      throw new Error("Encryption key is not available")
    }

    this.logger.info("DataEngine: Saving items", {
      count: items.length,
      itemIds: items.map((item) => item.itemId),
    })

    await this.itemProcessor.processItemUpdates(
      items,
      true, // sendToServer
      SyncMessageType.UPDATE_ITEMS,
    )
  }

  /**
   * Get item by ID
   */
  async getItem(id: string): Promise<DecryptedItemData | null> {
    return this.dataStorage.getDecryptedItemData(id)
  }

  /**
   * Delete item by ID
   */
  async deleteItem(itemId: string): Promise<void> {
    if (!this.userIsLoggedIn) {
      throw new Error("User must be authenticated to delete items")
    }

    if (!this.encryptionKey) {
      throw new Error("Encryption key is not available")
    }

    this.logger.info("DataEngine: Deleting item", { itemId })
    await this.itemProcessor.deleteItem(itemId, true)
  }

  /**
   * Update user settings
   */
  async updateUserSettings(
    settings: Partial<UserSettings>,
  ): Promise<UserSettings> {
    if (!this.userIsLoggedIn) {
      throw new Error("User must be authenticated to update settings")
    }

    this.logger.info("DataEngine: Updating user settings", settings)
    const updatedSettings = await this.dataStorage.updateUserSettings(settings)

    // Emit settings update
    this.emit(DataEngineEvents.SETTINGS_UPDATED, updatedSettings)

    return updatedSettings
  }

  /**
   * Update global tags list
   */
  async updateGlobalTags(tags: Tag[]): Promise<void> {
    if (!this.userIsLoggedIn) {
      throw new Error("User must be authenticated to update global tags")
    }

    if (!this.encryptionKey) {
      throw new Error("Encryption key is not available")
    }

    this.logger.info("DataEngine: Updating global tags", {
      tagCount: tags.length,
    })

    const globalTagsId = this.dataStorage.getGlobalTagsItemId()

    await this.itemProcessor.processItemUpdates([
      {
        itemId: globalTagsId,
        type: SyncItemType.Tags,
        content: { tags },
      },
    ])
  }

  /**
   * Get current data storage instance (for socket handlers)
   */
  getDataStorage(): ClientDataStorageEngine {
    return this.dataStorage
  }

  /**
   * Update items from server and refresh state
   */
  async updateItemsFromServer(items: any[]): Promise<void> {
    await this.dataStorage.updateItemsFromServer(items)
    // Data will be refreshed via ClientData events
  }

  /**
   * Get last sync timestamp
   */
  getLastSyncTimestamp(): number {
    return this.dataStorage.getLastSyncTimestamp()
  }

  /**
   * Set last sync timestamp
   */
  async setLastSyncTimestamp(timestamp: number): Promise<void> {
    await this.dataStorage.setLastSyncTimestamp(timestamp)
  }

  /**
   * Setup listeners for ClientData events to update state
   */
  private setupClientDataListeners(): void {
    // Clear any existing listeners first
    this.dataStorage.clearListeners()

    this.dataStorage.on(
      ClientDataEvents.ITEM_UPDATED,
      async (itemId: string, item: any) => {
        this.logger.debug("DataEngine: Item updated", {
          itemId,
          type: item.type,
        })

        if (item.type === SyncItemType.Note) {
          await this.loadAndEmitNotes()
        } else if (item.type === SyncItemType.Task) {
          await this.loadAndEmitTasks()
        } else if (item.type === SyncItemType.Tags) {
          await this.loadAndEmitTags()
        }
      },
    )
  }

  /**
   * Load and emit all data types
   */
  private async loadAndEmitAllData(): Promise<void> {
    await Promise.all([
      this.loadAndEmitNotes(),
      this.loadAndEmitTasks(),
      this.loadAndEmitTags(),
      this.loadAndEmitSettings(),
    ])
  }

  /**
   * Load notes from storage and emit update
   */
  private async loadAndEmitNotes(): Promise<void> {
    if (!this.userIsLoggedIn) {
      this.emit(DataEngineEvents.NOTES_UPDATED, [])
      return
    }

    try {
      const noteItems = this.dataStorage.getItemsByType(SyncItemType.Note)

      if (Object.keys(noteItems).length === 0) {
        this.emit(DataEngineEvents.NOTES_UPDATED, [])
        return
      }

      const notes: Note[] = await Promise.all(
        Object.values(noteItems).map(async (item) => {
          const decryptedData = await this.dataStorage.getDecryptedItemData(
            item.id,
          )
          return {
            id: item.id,
            title: decryptedData?.title || "Untitled Note",
            content: decryptedData?.content || "",
            tags: decryptedData?.tags || [],
            createdAt: item.createdAt,
            updatedAt: item.updatedAt,
          }
        }),
      )

      this.emit(DataEngineEvents.NOTES_UPDATED, notes)
    } catch (error) {
      this.logger.error("DataEngine: Failed to load notes", { error })
      this.emit(DataEngineEvents.NOTES_UPDATED, [])
    }
  }

  /**
   * Load tasks from storage and emit update
   */
  private async loadAndEmitTasks(): Promise<void> {
    if (!this.userIsLoggedIn || !this.encryptionKey) {
      this.emit(DataEngineEvents.TASKS_UPDATED, [])
      return
    }

    try {
      const items = this.dataStorage.getAllItems()
      const taskItems = Object.entries(items).filter(
        ([_, item]) => item.type === SyncItemType.Task,
      )

      const tasksPromises = taskItems.map(async ([id, item]) => {
        const decrypted = await this.dataStorage.getDecryptedItemData(id)
        if (!decrypted) return null
        return {
          id,
          index: 0, // Default index value for backwards compatibility
          ...decrypted,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
        }
      })

      const tasksResults = await Promise.all(tasksPromises)
      const validTasks = tasksResults.filter(
        (task): task is Task => task !== null && !task.inBin,
      )

      this.emit(DataEngineEvents.TASKS_UPDATED, validTasks)
    } catch (error) {
      this.logger.error("DataEngine: Failed to load tasks", { error })
      this.emit(DataEngineEvents.TASKS_UPDATED, [])
    }
  }

  /**
   * Load tags from storage and emit update
   */
  private async loadAndEmitTags(): Promise<void> {
    if (!this.userIsLoggedIn) {
      this.emit(DataEngineEvents.TAGS_UPDATED, [])
      return
    }

    try {
      const globalTagsId = this.dataStorage.getGlobalTagsItemId()
      const globalTagsData =
        await this.dataStorage.getDecryptedItemData(globalTagsId)

      if (globalTagsData?.tags && Array.isArray(globalTagsData.tags)) {
        this.emit(DataEngineEvents.TAGS_UPDATED, globalTagsData.tags)
      } else {
        this.emit(DataEngineEvents.TAGS_UPDATED, [])
      }
    } catch (error) {
      this.logger.error("DataEngine: Failed to load tags", { error })
      this.emit(DataEngineEvents.TAGS_UPDATED, [])
    }
  }

  /**
   * Load settings from storage and emit update
   */
  private async loadAndEmitSettings(): Promise<void> {
    try {
      const settings = await this.dataStorage.getUserSettings()
      this.emit(DataEngineEvents.SETTINGS_UPDATED, settings)
    } catch (error) {
      this.logger.error("DataEngine: Failed to load settings", { error })
      this.emit(DataEngineEvents.SETTINGS_UPDATED, DEFAULT_USER_SETTINGS)
    }
  }
}
